// ============================================================================
// HoneyTouch — unified inbox / comm center (frontend).
//
// One inbox across every SignalHouse + Twilio number. Left pane lists recent
// conversations (grouped by customer number); clicking one loads the full merged
// thread on the right, newest at the bottom, with a reply composer.
//
// COMPLIANCE IS VISIBLE: the server gates every reply (suppression / opt-out /
// quiet hours). If a reply is blocked, we surface the exact reasons in a .msg.bad
// right under the composer rather than pretending it sent.
// ============================================================================
import { api } from '../lib/api.js'
import { esc } from '../lib/view.js'
import { fmtPhone, etDT } from '../lib/format.js'

const PROVIDER_LABEL = { signalhouse: 'SignalHouse', twilio: 'Twilio' }

export function renderTouch(el) {
  el.innerHTML = `
    <div class="card pad">
      <div class="toolbar">
        <div class="grow">
          <div class="mod-h">HoneyTouch</div>
          <div class="mod-sub" style="margin:0">Unified inbox across your SignalHouse numbers — replies are consent &amp; quiet-hours gated</div>
        </div>
        <button id="tc-refresh" class="btn ghost" type="button">Refresh</button>
      </div>

      <!-- Filter bar: chips (with live counts) + number / date-range / search -->
      <div class="toolbar" style="gap:10px;flex-wrap:wrap;margin:4px 0 12px">
        <div id="tc-chips" style="display:flex;gap:6px;flex-wrap:wrap"></div>
        <div style="display:flex;gap:8px;align-items:center;margin-left:auto;flex-wrap:wrap">
          <select id="tc-number" class="h-input" style="width:auto">
            <option value="">All numbers</option>
          </select>
          <select id="tc-range" class="h-input" style="width:auto">
            <option value="1">Today</option>
            <option value="7" selected>Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="custom">Custom range…</option>
          </select>
          <span id="tc-custom" style="display:none;gap:6px;align-items:center">
            <span class="mod-sub" style="margin:0">From</span>
            <input id="tc-date-from" type="date" class="h-input" style="width:auto" aria-label="From date">
            <span class="mod-sub" style="margin:0">To</span>
            <input id="tc-date-to" type="date" class="h-input" style="width:auto" aria-label="To date">
          </span>
          <input id="tc-search" class="h-input" type="search" placeholder="Search phone or text…" style="width:180px">
        </div>
      </div>

      <div class="tc-panes">
        <!-- Left: conversation list -->
        <div class="tc-list" style="display:flex;flex-direction:column;border:1px solid var(--line);border-radius:var(--r);overflow:hidden;background:var(--surface-2)">
          <div id="tc-list" style="overflow-y:auto;max-height:640px">
            <div class="empty">Loading conversations…</div>
          </div>
        </div>

        <!-- Right: selected thread + composer -->
        <div style="flex:1;min-width:0;display:flex;flex-direction:column;border:1px solid var(--line);border-radius:var(--r);overflow:hidden">
          <div id="tc-thread-head" style="padding:12px 16px;border-bottom:1px solid var(--line);font-weight:800;display:none"></div>
          <div id="tc-thread" style="flex:1;overflow-y:auto;max-height:520px;padding:16px;display:flex;flex-direction:column;gap:10px">
            <div class="empty">Select a conversation to view the thread.</div>
          </div>
          <div id="tc-composer" style="border-top:1px solid var(--line);padding:12px 16px;display:none">
            <div id="tc-from" class="mod-sub" style="margin:0 0 6px"></div>
            <textarea id="tc-reply" class="h-input" placeholder="Type a reply… Reply STOP to opt out." style="min-height:64px"></textarea>
            <div style="display:flex;align-items:center;gap:10px;margin-top:8px">
              <button id="tc-send" class="btn" type="button">Send</button>
              <div id="tc-msg" class="msg" style="margin:0"></div>
            </div>
          </div>
        </div>
      </div>
    </div>`

  const $ = (id) => el.querySelector(id)
  const listEl = $('#tc-list')
  const threadEl = $('#tc-thread')
  const headEl = $('#tc-thread-head')
  const composerEl = $('#tc-composer')
  const fromEl = $('#tc-from')
  const replyEl = $('#tc-reply')
  const btnSend = $('#tc-send')
  const btnRefresh = $('#tc-refresh')
  const msgEl = $('#tc-msg')
  const chipsEl = $('#tc-chips')
  const numberEl = $('#tc-number')
  const dateFromEl = $('#tc-date-from')
  const dateToEl = $('#tc-date-to')
  const rangeEl = $('#tc-range')
  const customEl = $('#tc-custom')
  const searchEl = $('#tc-search')

  // Filter chips → server `filter` values (labels mirror the legacy inbox).
  const CHIPS = [
    { key: 'review', label: 'To Review' },
    { key: 'optout', label: 'Opt-outs' },
    { key: 'help', label: 'Help' },
    { key: 'replies', label: 'All replies' },
    { key: 'all', label: 'All' },
  ]
  const CAT_TAG = { optout: 'Opt-out', help: 'Help' }

  // Default the date range to the last 7 days (legacy default).
  const ymd = (d) => d.toISOString().slice(0, 10)
  const dfltFrom = ymd(new Date(Date.now() - 7 * 864e5))
  const dfltTo = ymd(new Date())
  dateFromEl.value = dfltFrom
  dateToEl.value = dfltTo

  // Per-thread reply routing + the active list filters.
  const state = {
    phone: null, from: null, provider: 'signalhouse',
    filter: 'all', number: '', dateFrom: dfltFrom, dateTo: dfltTo, q: '',
  }

  function setMsg(text, kind) {
    msgEl.className = 'msg' + (kind ? ' ' + kind : '')
    msgEl.textContent = text || ''
  }

  // ---- filter chips (live counts) -----------------------------------------
  function renderChips(counts) {
    const c = counts || {}
    chipsEl.innerHTML = CHIPS.map((chip) => {
      const n = typeof c[chip.key] === 'number' ? c[chip.key] : 0
      const active = state.filter === chip.key
      const style = active
        ? 'background:color-mix(in srgb, var(--turq) 22%, transparent);border-color:var(--turq);font-weight:800'
        : ''
      return `<button type="button" class="btn ghost" data-filter="${esc(chip.key)}" style="${style}">`
        + esc(chip.label) + ' <span style="opacity:.7;font-weight:700">' + esc(String(n)) + '</span></button>'
    }).join('')
    chipsEl.querySelectorAll('[data-filter]').forEach((b) => {
      b.addEventListener('click', () => {
        state.filter = b.getAttribute('data-filter')
        loadList()
      })
    })
  }

  // ---- our sending numbers (Number filter dropdown) -----------------------
  async function loadNumbers() {
    try {
      const d = await api.get('/api/numbers')
      const nums = Array.isArray(d.numbers) ? d.numbers : []
      numberEl.innerHTML = '<option value="">All numbers</option>'
        + nums.map((n) => `<option value="${esc(n.phone)}">${esc(n.label ? n.label + ' · ' : '')}${esc(fmtPhone(n.phone))}</option>`).join('')
      numberEl.value = state.number
    } catch { /* keep the "All numbers" default */ }
  }

  // ---- conversation list --------------------------------------------------
  async function loadList() {
    listEl.innerHTML = '<div class="empty">Loading conversations…</div>'
    try {
      const p = new URLSearchParams()
      p.set('filter', state.filter)
      if (state.number) p.set('number', state.number)
      if (state.dateFrom) p.set('from', state.dateFrom)
      if (state.dateTo) p.set('to', state.dateTo)
      if (state.q) p.set('q', state.q)
      const data = await api.get('/api/inbox?' + p.toString())
      renderChips(data.counts) // counts are window totals — always refresh the chips
      const threads = Array.isArray(data.threads) ? data.threads : []
      if (!threads.length) {
        listEl.innerHTML = '<div class="empty">No conversations match this filter.</div>'
        return
      }
      listEl.innerHTML = threads.map(renderRow).join('')
      // Wire clicks.
      listEl.querySelectorAll('[data-phone]').forEach((row) => {
        row.addEventListener('click', () => selectThread(row.getAttribute('data-phone')))
      })
      // Keep the current selection highlighted after a refresh.
      if (state.phone) highlight(state.phone)
    } catch (e) {
      renderChips(null)
      listEl.innerHTML = '<div class="msg bad" style="padding:16px">Could not load conversations: ' + esc(e.message) + '</div>'
    }
  }

  function renderRow(t) {
    const time = t.lastAt ? etDT(t.lastAt) : ''
    const dot = t.unreadHint
      ? '<span title="Awaiting reply" style="flex:none;width:8px;height:8px;border-radius:50%;background:var(--pink)"></span>'
      : ''
    const tag = CAT_TAG[t.category]
      ? '<span style="flex:none;font-size:10px;font-weight:800;padding:1px 6px;border-radius:999px;background:color-mix(in srgb, var(--pink) 16%, transparent);color:var(--text-2)">' + esc(CAT_TAG[t.category]) + '</span>'
      : ''
    return `
      <div data-phone="${esc(t.phone)}" style="padding:12px 14px;border-bottom:1px solid var(--line-soft);cursor:pointer;display:flex;flex-direction:column;gap:3px">
        <div style="display:flex;align-items:center;gap:7px">
          ${dot}
          <span style="font-weight:800;font-size:14px">${esc(t.label || fmtPhone(t.phone))}</span>
          ${tag}
          <span style="margin-left:auto;font-size:11px;color:var(--muted)">${esc(time)}</span>
        </div>
        <div style="font-size:12.5px;color:var(--text-2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(t.lastMessage || '')}</div>
        <div style="font-size:11px;color:var(--muted)">${esc(PROVIDER_LABEL[t.provider] || t.provider || '')} · ${esc(String(t.count || 0))} msg</div>
      </div>`
  }

  function highlight(phone) {
    listEl.querySelectorAll('[data-phone]').forEach((row) => {
      const on = row.getAttribute('data-phone') === phone
      row.style.background = on ? 'color-mix(in srgb, var(--turq) 12%, transparent)' : ''
    })
  }

  // ---- thread view --------------------------------------------------------
  async function selectThread(phone) {
    state.phone = phone
    highlight(phone)
    setMsg('')
    headEl.style.display = ''
    headEl.textContent = fmtPhone(phone)
    composerEl.style.display = 'none'
    threadEl.innerHTML = '<div class="empty">Loading thread…</div>'
    try {
      const data = await api.get('/api/inbox?thread=' + encodeURIComponent(phone))
      const messages = Array.isArray(data.messages) ? data.messages : []
      headEl.textContent = data.label || fmtPhone(phone)
      renderThread(messages)
      deriveRouting(messages)
    } catch (e) {
      threadEl.innerHTML = '<div class="msg bad" style="padding:16px">Could not load thread: ' + esc(e.message) + '</div>'
    }
  }

  function renderThread(messages) {
    if (!messages.length) {
      threadEl.innerHTML = '<div class="empty">No messages in this thread.</div>'
      return
    }
    threadEl.innerHTML = messages.map(renderBubble).join('')
    threadEl.scrollTop = threadEl.scrollHeight // pin to newest
  }

  function renderBubble(m) {
    const inbound = m.direction === 'in'
    const align = inbound ? 'flex-start' : 'flex-end'
    const tint = inbound
      ? 'color-mix(in srgb, var(--turq) 14%, var(--surface))'
      : 'color-mix(in srgb, var(--pink) 14%, var(--surface))'
    const meta = [PROVIDER_LABEL[m.provider] || m.provider, m.at ? etDT(m.at) : '', m.status]
      .filter(Boolean).map(esc).join(' · ')
    return `
      <div style="align-self:${align};max-width:78%;display:flex;flex-direction:column;gap:3px">
        <div style="background:${tint};border:1px solid var(--line-soft);border-radius:var(--r);padding:9px 12px;font-size:14px;line-height:1.4;white-space:pre-wrap;word-break:break-word">${esc(m.body || '')}</div>
        <div style="font-size:10.5px;color:var(--muted);text-align:${inbound ? 'left' : 'right'}">${meta}</div>
      </div>`
  }

  // Derive reply routing (from-number + provider) from the newest OWNED message:
  // outbound → the number we sent from; inbound → the number they texted (our line).
  function deriveRouting(messages) {
    let from = null, provider = 'signalhouse'
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      const owned = m.direction === 'out' ? m.from : m.to
      if (owned) { from = owned; provider = m.provider || 'signalhouse'; break }
    }
    state.from = from
    state.provider = provider === 'twilio' ? 'twilio' : 'signalhouse'
    composerEl.style.display = ''
    fromEl.innerHTML = from
      ? 'Replying from <b>' + esc(fmtPhone(from)) + '</b> · ' + esc(PROVIDER_LABEL[state.provider])
      : '<span style="color:var(--bad)">No sending line detected for this thread.</span>'
    btnSend.disabled = !from
  }

  // ---- send ---------------------------------------------------------------
  async function send() {
    const body = replyEl.value.trim()
    if (!state.phone) { setMsg('Select a conversation first.', 'bad'); return }
    if (!state.from) { setMsg('No sending line detected for this thread.', 'bad'); return }
    if (!body) { setMsg('Type a message to send.', 'bad'); return }

    btnSend.disabled = true
    const prev = btnSend.textContent
    btnSend.textContent = 'Sending…'
    setMsg('Sending…')
    try {
      const res = await api.post('/api/inbox', {
        to: state.phone, from: state.from, body, provider: state.provider,
      })
      if (res.sent) {
        replyEl.value = ''
        setMsg('Sent.', 'ok')
        await selectThread(state.phone) // reload with the new message
        loadList() // refresh snippet/order on the left
      } else if (Array.isArray(res.blocked)) {
        // Compliance block — show exactly why (visible compliance).
        setMsg('Blocked by compliance: ' + res.blocked.map(esc).join(', '), 'bad')
      } else {
        setMsg('Not sent: ' + esc(res.error || 'unknown error'), 'bad')
      }
    } catch (e) {
      setMsg('Error: ' + (e?.message || String(e)), 'bad')
    } finally {
      btnSend.disabled = !state.from
      btnSend.textContent = prev
    }
  }

  btnSend.addEventListener('click', send)
  btnRefresh.addEventListener('click', loadList)

  // ---- filter-bar wiring --------------------------------------------------
  numberEl.addEventListener('change', () => { state.number = numberEl.value; loadList() })
  // Preset range → set From/To. "custom" reveals the date inputs.
  function applyRange(days) {
    const from = ymd(new Date(Date.now() - days * 864e5))
    const to = ymd(new Date())
    dateFromEl.value = from; dateToEl.value = to
    state.dateFrom = from; state.dateTo = to
  }
  rangeEl.addEventListener('change', () => {
    if (rangeEl.value === 'custom') { customEl.style.display = 'inline-flex'; return }
    customEl.style.display = 'none'
    applyRange(parseInt(rangeEl.value) || 7)
    loadList()
  })
  // Editing a custom date switches the preset to "Custom range…".
  dateFromEl.addEventListener('change', () => { rangeEl.value = 'custom'; customEl.style.display = 'inline-flex'; state.dateFrom = dateFromEl.value; loadList() })
  dateToEl.addEventListener('change', () => { rangeEl.value = 'custom'; customEl.style.display = 'inline-flex'; state.dateTo = dateToEl.value; loadList() })
  let searchTimer
  searchEl.addEventListener('input', () => {
    clearTimeout(searchTimer)
    searchTimer = setTimeout(() => { state.q = searchEl.value.trim(); loadList() }, 300)
  })

  loadNumbers()
  loadList()
}
